<template>
  <div>
    <page-title-area>
      <h1>{{$t('title')}}</h1>
    </page-title-area>
    <div class="filter-area d-flex align-center mb-4 py-1">
      <month-picker
        v-model="currentMonth"
        :max-date="todayMonthString"
        @input="onMonthChanged"
        data-testid="month-picker"
      />
      <select-box
        class="flex-grow-0"
        style="width: 300px"
        v-model="selectedItemId"
        :items="selectItems"
        :label="$mpt($t('field.sensor'))"
        data-testid="item-select"
      />
      <insect-type-select-box
        v-model="insectTypeFilter"
        :null-option="$t('all_insects_option')"
        :label="$t('field.insect_type')"
        data-testid="insect-type-select"
      />
    </div>
    <div style="position: relative;">
      <v-overlay v-if="isLoading" absolute color="white" opacity="0.9">
        <page-load-spinner />
      </v-overlay>
      <v-row dense>
        <v-col sm="6" md="4" lg="3" xl="2">
          <v-card class="graph-area" style="height: 200px">
            <div>
              <h2>{{ $t('total_count') }}</h2>
            </div>
            <total-count-panel
              :events="panelData.totalCountPanel"
              :month="currentMonth"
            />
          </v-card>
        </v-col>
      <v-col cols="12" sm="12" md="8" lg="8" xl="7">
        <v-card class="graph-area d-flex flex-column" style="height: 200px">
          <div>
            <h2>{{ $t('alert') }}</h2>
          </div>
          <hr class="mx-n4">
          <sensor-notice-history
            class="mx-n4 mb-n4"
            :events="panelData.noticeHistory"
            :sensors="sensors"
          />
        </v-card>
      </v-col>
      <v-col sm="12" lg="12" xl="6">
        <v-row dense>
          <v-col cols="12">
            <v-card class="graph-area" style="height: 360px">
              <div class="d-flex">
                <h2>{{ $t('insect_count') }}</h2>
                <v-spacer />
                <alert-legend v-if="showAlertPeriods"/>
              </div>
              <daily-insect-count-chart
                style="height: 290px"
                :events="panelData.dailyInsectCountChart"
                :graphs="comparedWithPreviousMonthGraphs"
                :y-max="dailyInsectCountYMax"
                :show-alert-periods="showAlertPeriods"
                :on-chart-click="handleChartClick"
                @max-count-updated="value => updateMaxDailyInsectCount(0, value)"
              />
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col sm="6" md="12" lg="10" xl="6">
        <v-card class="graph-area" style="height: 360px;min-width: 440px;max-width: 920px;">
          <div>
            <h2>{{ $t('top5') }}</h2>
          </div>
          <top5-insects-panel :events="panelData.top5InsectsPanel" style="height: 290px" />
        </v-card>
      </v-col>
      <v-col sm="12" lg="2" class="d-xl-none" />
      <v-col cols="12" md="4">
        <v-card class="graph-area" style="height: 320px">
          <div>
            <h2>{{ $t('hourly_insect_count') }}</h2>
          </div>
          <hourly-bar-chart
            style="height: 250px"
            class="count-graph"
            :events="panelData.hourlyBarChart"
          />
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card class="graph-area" style="height: 320px">
          <div>
            <h2>{{ $t('weekly_insect_count') }}</h2>
          </div>
          <weekday-bar-chart
            style="height: 250px"
            class="count-graph"
            :events="panelData.weekdayBarChart"
          />
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card class="graph-area" style="height: 320px">
          <div>
            <h2>{{ $t('weekly_and_hourly_insect_count') }}</h2>
          </div>
          <weekday-hour-matrix-chart
            style="height: 250px"
            :events="panelData.weekdayHourMatrixChart"
          />
        </v-card>
      </v-col>
      </v-row>
    </div>
  </div>
</template>

<i18n lang="yaml">
ja:
  current_month: 当月
  total_count: 総捕獲数
  insect_count: 捕獲数
  previous_month: 前月
  same_month_previous_year: 昨年同月
  hourly_insect_count: 時間帯別 捕獲数
  weekly_insect_count: 曜日別 捕獲数
  weekly_and_hourly_insect_count: 曜日 x 時間帯別 捕獲数
  sum: 合計 ({sensorCount}台)
  top5: 捕獲上位5種
  title: モニタリングサマリー
  all_insects_option: 全種合計
  alert: アラート
  field:
    sensor: センサー
    insect_type: 虫種
  error:
    data_fetch_failed: データの取得に失敗しました。しばらくしてから再度お試しください。
    network_error: ネットワークエラーが発生しました。インターネット接続を確認してください。
    server_error: サーバーエラーが発生しました。管理者にお問い合わせください。

en:
  current_month: Current Month
  total_count: Total Insects Captured
  insect_count: Total Insects Captured
  previous_month: Previous Month
  same_month_previous_year: Previous Year
  hourly_insect_count: Hourly Insect Activity
  weekly_insect_count: Insect Activity by Weekday
  weekly_and_hourly_insect_count: Insect Activity by Weekday and Hour
  sum: Sum ({sensorCount} sensors)
  top5: Top 5 Species
  title: Monitoring Summary
  all_insects_option: Total of all species
  alert: Alerts
  field:
    sensor: Sensor
    insect_type: Insect Type
  error:
    data_fetch_failed: Failed to fetch data. Please try again later.
    network_error: Network error occurred. Please check your internet connection.
    server_error: Server error occurred. Please contact administrator.
</i18n>

<script>
import _ from 'lodash';
import {
  endOfMonth as dateFnsEndOfMonth,
  format,
  getUnixTime,
  parse,
  startOfMonth as dateFnsStartOfMonth,
} from 'date-fns';
import AbortControl from '@/libs/AbortControl';
import theme from '@/libs/theme';
import { addToMonthString } from '@/libs/time';
import Notifications from '@/mixins/notifications';
import dateTimeFormatting from '@/mixins/dateTimeFormatting';

import MonthPicker from '@/components/atoms/MonthPicker';
import SelectBox from '@/components/atoms/SelectBox';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import PageLoadSpinner from '@/components/atoms/PageLoadSpinner';
import InsectTypeSelectBox from '@/components/molecules/InsectTypeSelectBox';
import SensorNoticeHistory from './SensorNoticeHistory';
import AlertLegend from './AlertLegend';
import { calcInsectCountIncreases, eventsInMonth } from './chartCalculations';
import DailyInsectCountChart from './DailyInsectCountChart';
import HourlyBarChart from './HourlyBarChart';
import Top5InsectsPanel from './Top5InsectsPanel';
import TotalCountPanel from './TotalCountPanel';
import WeekdayHourMatrixChart from './WeekdayHourMatrixChart';
import WeekdayBarChart from './WeekdayBarChart';

export const sortAndAddChartInfo = (events) => {
  const result = _.sortBy(events, 'timestamp').map((event) => ({
    ...event,
    chartInfo: {
      alert: event.sensor_threshold !== 0 && event.count >= event.sensor_threshold,
      increaseAlert: event.sensor_increase_threshold !== 0 &&
        event.count_difference >= event.sensor_increase_threshold,
      count: event.count,
      countIncrease: Math.max(event.count_difference, 0),
      insectCountIncreases: {},
    },
  }));

  for (let i = 1; i < result.length; i += 1) {
    const previousEvent = result[i - 1];
    const event = result[i];
    event.chartInfo.insectCountIncreases = calcInsectCountIncreases(
      previousEvent.insect_counts,
      event.insect_counts,
    );
  }

  return result;
};

export const applySelectionFilter = (eventsByItemId, selectedItem) => {
  if (!selectedItem) {
    return Object.values(eventsByItemId).flat();
  }

  return eventsByItemId?.[selectedItem.id] || [];
};

export const applyInsectTypeFilter = (events, insectType) => {
  if (!insectType) {
    return events;
  }

  const result = events.map((event) => {
    const filteredEvent = {
      ...event,
      chartInfo: {
        ...event.chartInfo,
        count: event.insect_counts[insectType] || 0,
        insectCountIncreases: {},
      },
    };

    const countIncrease = event.chartInfo.insectCountIncreases[insectType] || 0;
    if (countIncrease > 0) {
      filteredEvent.chartInfo.insectCountIncreases[insectType] = countIncrease;
    }

    return filteredEvent;
  });

  for (let i = 1; i < result.length; i += 1) {
    const event = result[i];
    const previousEvent = result[i - 1];
    event.chartInfo.countIncrease = Math.max(
      event.chartInfo.count - previousEvent.chartInfo.count,
      0,
    );
  }

  return result;
};

export default {
  name: 'dashboard',
  mixins: [
    Notifications,
    dateTimeFormatting,
  ],
  props: {
  },
  components: {
    AlertLegend,
    DailyInsectCountChart,
    HourlyBarChart,
    InsectTypeSelectBox,
    MonthPicker,
    SensorNoticeHistory,
    PageLoadSpinner,
    PageTitleArea,
    SelectBox,
    Top5InsectsPanel,
    TotalCountPanel,
    WeekdayBarChart,
    WeekdayHourMatrixChart,
  },
  data() {
    return {
      currentMonth: format(new Date(), 'yyyy-MM'),
      sortedEventsByItemId: {},
      maxDailyInsectCounts: [0, 0],
      insectTypeFilter: null,
      selectedItemId: null,
      isLoading: false,
      abortControl: new AbortControl(),
    };
  },
  async created() {
    await this.executeWithAbortControl(() => this.fetchAllEventsData());
  },
  computed: {
    todayMonthString() {
      return format(new Date(), 'yyyy-MM');
    },
    currentMonthPeriod() {
      return this.createPeriod(0);
    },
    previousMonthPeriod() {
      return this.createPeriod(-1);
    },
    previousYearMonthPeriod() {
      return this.createPeriod(-12);
    },
    // 先月未満の最新データ取得用（先月開始時点より前の全データ）
    beforePreviousMonthPeriod() {
      return this.createBeforePeriod(-1);
    },
    // 昨年同月未満の最新データ取得用（昨年同月開始時点より前の全データ）
    beforePreviousYearMonthPeriod() {
      return this.createBeforePeriod(-12);
    },
    comparedWithPreviousMonthGraphs() {
      return [
        {
          month: this.currentMonth,
          color: theme.primaryColor,
          label: this.$t('current_month'),
        },
        {
          month: addToMonthString(this.currentMonth, -1),
          color: '#19d276',
          label: this.$t('previous_month'),
        },
        {
          month: addToMonthString(this.currentMonth, -12),
          color: 'black',
          label: this.$t('same_month_previous_year'),
        },
      ];
    },
    dailyInsectCountYMax() {
      const totalMaxCount = Math.max(...this.maxDailyInsectCounts);

      let roundTo = 5;
      if (totalMaxCount >= 100) {
        roundTo = 20;
      }

      return this.roundToNext(totalMaxCount, roundTo);
    },
    sensors() {
      return this.$store.getters.getSensors.filter(sensor => sensor.type === 'fly_scan');
    },
    selectItems() {
      return [
        { value: null, text: this.$t('sum', { sensorCount: this.sensors.length }) },
        ...this.sensors.map(sensor => ({ value: sensor.id, text: sensor.name })),
      ];
    },
    selectedItem() {
      if (!this.selectedItemId) {
        return null;
      }

      return this._.find(this.sensors, { id: this.selectedItemId });
    },
    panelData() {
      const allMonthEvents = applyInsectTypeFilter(
        applySelectionFilter(this.sortedEventsByItemId, this.selectedItem),
        this.insectTypeFilter,
      );

      const currentMonthEvents = eventsInMonth(
        this.currentMonth,
        allMonthEvents,
      );

      return {
        totalCountPanel: allMonthEvents,
        noticeHistory: currentMonthEvents,
        dailyInsectCountChart: allMonthEvents,
        top5InsectsPanel: currentMonthEvents,
        hourlyBarChart: currentMonthEvents,
        weekdayBarChart: currentMonthEvents,
        weekdayHourMatrixChart: currentMonthEvents,
      };
    },
    displayingSum() {
      return this.selectedItemId === null;
    },
    showAlertPeriods() {
      return !this.displayingSum;
    },
  },
  methods: {
    createPeriod(monthOffset) {
      const targetMonth = addToMonthString(this.currentMonth, monthOffset);
      const monthDate = parse(targetMonth, 'yyyy-MM', new Date());
      const start = getUnixTime(dateFnsStartOfMonth(monthDate));
      // 閉区間: 月末 23:59:59 を含む
      const end = getUnixTime(dateFnsEndOfMonth(monthDate));

      return {
        from: start,
        to: end,
      };
    },
    // 特定月より前の全データ取得用の期間を作成
    createBeforePeriod(monthOffset) {
      const targetMonth = addToMonthString(this.currentMonth, monthOffset);
      // 半開区間: 対象月の月初を to に指定し「対象月以前」を表す（対象月自体は含まない）
      const startOfTargetMonthTs = getUnixTime(dateFnsStartOfMonth(parse(targetMonth, 'yyyy-MM', new Date())));

      return {
        from: 0,
        to: startOfTargetMonthTs,
      };
    },
    async onMonthChanged(newMonth) {
      this.currentMonth = newMonth;
      await this.executeWithAbortControl(() => this.fetchAllEventsData());
    },
    async fetchSensorEvents(sensorId, signal) {
      // Fetch data for 4 periods in parallel:
      // 1. Current and previous month
      // 2. Latest data before previous month (1 event)
      // 3. Previous year same month
      // 4. Latest data before previous year same month (1 event)
      const [
        currentResult,
        previousResult,
        beforePreviousResult,
        previousYearResult,
        beforePreviousYearResult,
      ] = await Promise.all([
        this.$store.dispatch('getEvents', {
          sensorId,
          params: {
            ...this.currentMonthPeriod,
          },
          options: { signal },
        }),
        this.$store.dispatch('getEvents', {
          sensorId,
          params: {
            ...this.previousMonthPeriod,
          },
          options: { signal },
        }),
        this.$store.dispatch('getEvents', {
          sensorId,
          params: {
            ...this.beforePreviousMonthPeriod,
            limit: 1,
            order: 'desc',
          },
          options: { signal },
        }),
        this.$store.dispatch('getEvents', {
          sensorId,
          params: {
            ...this.previousYearMonthPeriod,
          },
          options: { signal },
        }),
        this.$store.dispatch('getEvents', {
          sensorId,
          params: {
            ...this.beforePreviousYearMonthPeriod,
            limit: 1,
            order: 'desc',
          },
          options: { signal },
        }),
      ]);

      // Combine all events for TotalCountPanel to calculate comparisons
      const allEvents = [
        ...(currentResult.events || []),
        ...(previousResult.events || []),
        ...(beforePreviousResult.events || []),
        ...(previousYearResult.events || []),
        ...(beforePreviousYearResult.events || []),
      ];

      // Return processed events array instead of setting sortedEventsByItemId
      return sortAndAddChartInfo(allEvents);
    },
    roundToNext(value, increment) {
      return Math.ceil(value / increment) * increment;
    },
    updateMaxDailyInsectCount(graphIndex, updatedValue) {
      const newValue = [...this.maxDailyInsectCounts];
      newValue[graphIndex] = updatedValue;
      this.maxDailyInsectCounts = newValue;
    },
    handleChartClick(dateString) {
      if (this.selectedItemId === null) {
        return;
      }
      this.navigateToActivity(dateString);
    },
    navigateToActivity(dateString) {
      const clickedDate = parse(dateString, 'yyyy-MM-dd', new Date());
      const from = this.formatDateForApi(clickedDate);
      const to = this.formatDateForApi(clickedDate);
      const queryParams = {
        timePeriod: JSON.stringify({ from, to }),
        sensorId: this.selectedItemId,
      };
      const url = this.$router.resolve({
        path: '/activities',
        query: queryParams,
      });
      window.open(url.href, '_blank');
    },
    async executeWithAbortControl(dataFetchFunction) {
      this.abortControl.newSignal();
      this.isLoading = true;

      try {
        await dataFetchFunction();
      } catch (error) {
        if (!AbortControl.isAbortError(error)) {
          if (!error.response) {
            this.notifyError('error.network_error');
          } else if (error.response.status >= 500) {
            this.notifyError('error.server_error');
          } else {
            this.notifyError('error.data_fetch_failed');
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchAllEventsData() {
      this.sortedEventsByItemId = {};
      await this.fetchSensorData();
    },
    async fetchSensorData() {
      await this.$store.dispatch('requestSensors', {
        signal: this.abortControl.signal,
      });

      const promises = this.sensors.map(async (sensor) => {
        const events = await this.fetchSensorEvents(sensor.id, this.abortControl.signal);
        return { sensorId: sensor.id, events };
      });

      const results = await Promise.all(promises);

      // 戻り値を使ってsortedEventsByItemIdを設定
      results.forEach(({ sensorId, events }) => {
        this.sortedEventsByItemId = {
          ...this.sortedEventsByItemId,
          [sensorId]: events || [],
        };
      });
    },
  },
  beforeDestroy() {
    this.abortControl.abort();
  },
};
</script>

<style scoped lang="sass">
.filter-area
  background-color: #fff

  & > *
    margin-left: 8px
    margin-right: 8px

.graph-area
  padding: 16px

  h2
    font-size: 1.25rem
    font-weight: normal
    padding-bottom: 16px
</style>
